import { ZuruflexApiError, type FlagChange, type FlagSummary } from '@zuruflex/api-client';
import { formatDateTimeIn, toLocale } from '@zuruflex/i18n';
import {
  compareVersions,
  emptyFlagState,
  FLAG_PLATFORMS,
  isFlagKey,
  validateFlagState,
  type FlagContext,
  type FlagEvaluation,
  type FlagPlatform,
  type FlagRule,
  type FlagState,
  type FlagSurface,
} from '@zuruflex/types';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  ErrorState,
  FormField,
  Input,
  PageHeader,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Switch,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
} from '@zuruflex/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowDown,
  ArrowUp,
  ChevronLeft,
  Copy,
  FlaskConical,
  Plus,
  RotateCcw,
  Trash2,
  Undo2,
} from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { FlagValueEditor } from '../components/FlagValueEditor.js';
import { api } from '../lib/api.js';
import { useAuth } from '../lib/auth.js';
import {
  blankRule,
  diffFlagStates,
  formatFlagValue,
  newRuleId,
  parseIdList,
  percentToRollout,
  relativeTime,
  rolloutToPercent,
  type FlagStateDiff,
} from '../lib/flags.js';

const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

/**
 * An edit in progress: the version it started from, and where it has got
 * to. Null while nothing is being edited, so the page shows the server's
 * state as-is and a background refetch lands without anyone's work being
 * overwritten — once there are edits, `base` is frozen and a save against
 * a newer row comes back 409 rather than quietly winning.
 */
interface Edit {
  base: FlagSummary;
  draft: FlagState;
}

type IdField = 'customerIds' | 'partnerIds' | 'partnerUserIds';
const ID_FIELDS: IdField[] = ['customerIds', 'partnerIds', 'partnerUserIds'];

/**
 * One flag: its switch, who it targets, a way to try the targeting before
 * saving it, and who changed it.
 *
 * The switch acts at once, for managers too — it is the incident lever.
 * Everything else is a draft saved as a whole, by an owner, behind a
 * summary of what is about to change: a rule edit reaches every app within
 * the minute, so it is worth one look before it goes.
 */
export function FlagDetailPage() {
  const { key = '' } = useParams();
  const { t, i18n } = useTranslation(['admin', 'common']);
  const locale = toLocale(i18n.language);
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const canToggle = user?.role === 'OWNER' || user?.role === 'MANAGER';
  const canEdit = user?.role === 'OWNER';

  const [edit, setEdit] = useState<Edit | null>(null);
  /** Bumped to remount the editors that keep their own text, e.g. on Discard. */
  const [revision, setRevision] = useState(0);
  /** Boxes whose text is not a value yet (half-typed JSON), by editor. */
  const [unparsed, setUnparsed] = useState<Record<string, string>>({});
  const [confirming, setConfirming] = useState<'save' | 'reset' | null>(null);

  const flag = useQuery({
    queryKey: ['flag', key],
    queryFn: () => api.getFlag(key),
  });

  const settle = (saved: FlagSummary | null) => {
    if (saved) queryClient.setQueryData(['flag', key], saved);
    void queryClient.invalidateQueries({ queryKey: ['flag', key] });
    void queryClient.invalidateQueries({ queryKey: ['flags'] });
  };

  /** Throws away the draft and shows whatever the server holds. */
  const discard = () => {
    setEdit(null);
    setUnparsed({});
    setRevision((n) => n + 1);
  };

  const save = useMutation({
    mutationFn: (input: { state: FlagState; version: number }) =>
      api.saveFlag(key, input.state, input.version),
    onSuccess: (saved) => {
      settle(saved);
      discard();
    },
    onSettled: () => setConfirming(null),
  });

  const reset = useMutation({
    mutationFn: () => api.resetFlag(key),
    onSuccess: () => {
      settle(null);
      discard();
    },
    onSettled: () => setConfirming(null),
  });

  const toggle = useMutation({
    mutationFn: (input: { enabled: boolean; version: number }) =>
      api.toggleFlag(key, input.enabled, input.version),
    onSuccess: (saved) => {
      settle(saved);
      // Rebase any draft onto the row the switch just wrote, so its other
      // edits survive and its next save is not refused as stale.
      setEdit((current) => {
        if (!current) return null;
        const draft = { ...current.draft, enabled: saved.state.enabled };
        return same(draft, saved.state) ? null : { base: saved, draft };
      });
    },
  });

  if (flag.error) {
    return <ErrorState error={flag.error} onRetry={() => void flag.refetch()} />;
  }
  if (flag.isPending) return <Skeleton className="h-96 w-full" />;

  const current = flag.data;
  const base = edit?.base ?? current;
  const draft = edit?.draft ?? current.state;
  const dirty = edit !== null;

  const change = (update: (state: FlagState) => FlagState) => {
    setEdit((prev) => {
      const from = prev?.base ?? current;
      const next = update(prev?.draft ?? current.state);
      // Editing back to where it started is not an edit.
      return same(next, from.state) ? null : { base: from, draft: next };
    });
  };
  const changeRule = (id: string, patch: Partial<FlagRule>) =>
    change((state) => ({
      ...state,
      rules: state.rules.map((rule) => (rule.id === id ? { ...rule, ...patch } : rule)),
    }));
  const markUnparsed = (editor: string, error: string | null) =>
    setUnparsed((prev) => {
      if (!error && !(editor in prev)) return prev;
      const next = { ...prev };
      if (error) next[editor] = error;
      else delete next[editor];
      return next;
    });

  const checked = validateFlagState(key, draft);
  const problem = Object.values(unparsed)[0] ?? (checked.ok ? null : checked.error);
  const canSave = canEdit && dirty && !problem && !save.isPending;

  const addRule = () =>
    change((state) => ({
      ...state,
      // A boolean rule almost always exists to serve the other answer.
      rules: [
        ...state.rules,
        blankRule(current.type === 'boolean' ? current.default !== true : state.fallthrough),
      ],
    }));
  const moveRule = (index: number, by: -1 | 1) =>
    change((state) => {
      const rules = [...state.rules];
      const [rule] = rules.splice(index, 1);
      if (rule) rules.splice(index + by, 0, rule);
      return { ...state, rules };
    });
  const duplicateRule = (index: number) =>
    change((state) => {
      const rules = [...state.rules];
      const source = rules[index];
      if (source) rules.splice(index + 1, 0, { ...source, id: newRuleId() });
      return { ...state, rules };
    });
  const deleteRule = (id: string) => {
    change((state) => ({ ...state, rules: state.rules.filter((rule) => rule.id !== id) }));
    setUnparsed((prev) => {
      const next = { ...prev };
      delete next[`rule:${id}`];
      return next;
    });
  };

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow={
          <Link
            to="/flags"
            className="inline-flex items-center gap-1 text-sm text-text-muted transition-colors hover:text-text"
          >
            <ChevronLeft className="size-4" />
            {t('flags.detail.back')}
          </Link>
        }
        title={current.label}
        description={
          <span className="inline-flex flex-wrap items-center gap-2">
            <span className="font-mono font-semibold text-text">{current.key}</span>
            <Badge variant="outline">{t(`flags.type.${current.type}`)}</Badge>
            {dirty ? <Badge variant="warning">{t('flags.detail.unsaved')}</Badge> : null}
          </span>
        }
        action={
          <div className="flex flex-wrap items-center gap-3">
            <label className="mr-2 inline-flex items-center gap-3 rounded-pill border border-border-default px-4 py-2 text-sm font-medium">
              <Switch
                checked={base.state.enabled}
                disabled={!canToggle || toggle.isPending}
                onCheckedChange={(enabled) => toggle.mutate({ enabled, version: base.version })}
              />
              {base.state.enabled ? t('flags.detail.on') : t('flags.detail.off')}
            </label>
            {canEdit ? (
              <>
                <Button
                  variant="ghost"
                  disabled={!current.managed || reset.isPending}
                  onClick={() => setConfirming('reset')}
                >
                  <RotateCcw />
                  {t('flags.detail.reset')}
                </Button>
                <Button variant="outline" disabled={!dirty} onClick={discard}>
                  <Undo2 />
                  {t('flags.detail.discard')}
                </Button>
                <Button disabled={!canSave} onClick={() => setConfirming('save')}>
                  {t('common:action.save')}
                </Button>
              </>
            ) : null}
          </div>
        }
      />

      {!canToggle ? (
        <Alert variant="info">{t('flags.readOnlyAgent')}</Alert>
      ) : !canEdit ? (
        <Alert variant="info">{t('flags.managerNote')}</Alert>
      ) : null}

      <WriteError
        error={save.error ?? toggle.error ?? reset.error}
        onReload={() => {
          save.reset();
          toggle.reset();
          reset.reset();
          discard();
          void flag.refetch();
        }}
      />

      <Card>
        <CardContent className="grid gap-6 pt-6 md:grid-cols-2 xl:grid-cols-[2fr_1fr_1fr_1fr]">
          <Fact label={t('flags.detail.description')}>
            <p className="text-sm">{current.description}</p>
          </Fact>
          <Fact label={t('flags.column.surfaces')}>
            <div className="flex flex-wrap gap-1">
              {current.surfaces.map((item) => (
                <Badge key={item}>{t(`flags.surface.${item}`)}</Badge>
              ))}
            </div>
          </Fact>
          <Fact label={t('flags.detail.default')}>
            <code className="break-all font-mono text-sm">{formatFlagValue(current.default, 120)}</code>
          </Fact>
          <Fact label={t('flags.detail.version')}>
            <p className="text-sm">
              {current.managed
                ? t('flags.detail.versionValue', {
                    version: base.version,
                    when: base.updatedAt ? formatDateTimeIn(base.updatedAt, locale) : '—',
                  })
                : t('flags.status.untouched')}
            </p>
          </Fact>
        </CardContent>
      </Card>

      <Tabs defaultValue="targeting">
        <TabsList>
          <TabsTrigger value="targeting">{t('flags.detail.targeting')}</TabsTrigger>
          <TabsTrigger value="history">{t('flags.history.title')}</TabsTrigger>
        </TabsList>

        <TabsContent value="targeting" className="pt-4">
          <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_420px]">
            <div className="min-w-0 space-y-6">
              {problem && dirty ? <Alert variant="danger">{problem}</Alert> : null}

              {!base.state.enabled ? (
                <Alert variant="warning">{t('flags.detail.offNote')}</Alert>
              ) : null}

              <Card>
                <CardHeader>
                  <CardTitle>{t('flags.detail.fallthrough')}</CardTitle>
                  <CardDescription>{t('flags.detail.fallthroughHint')}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="max-w-xl">
                    <FlagValueEditor
                      key={`${revision}-fallthrough`}
                      flag={current}
                      value={draft.fallthrough}
                      disabled={!canEdit}
                      onChange={(value) => change((state) => ({ ...state, fallthrough: value }))}
                      onInvalid={(error) => markUnparsed('fallthrough', error)}
                    />
                  </div>
                </CardContent>
              </Card>

              <section className="space-y-4">
                <div className="flex flex-wrap items-end justify-between gap-4">
                  <div>
                    <h2 className="text-lg font-semibold text-text">{t('flags.rule.title')}</h2>
                    <p className="mt-1 max-w-3xl text-sm text-text-muted">{t('flags.rule.help')}</p>
                  </div>
                  {canEdit ? (
                    <Button variant="outline" onClick={addRule}>
                      <Plus />
                      {t('flags.rule.add')}
                    </Button>
                  ) : null}
                </div>

                {draft.rules.length === 0 ? (
                  <p className="rounded-card border border-dashed border-border-default p-6 text-center text-sm text-text-muted">
                    {t('flags.rule.none')}
                  </p>
                ) : (
                  draft.rules.map((rule, index) => (
                    <RuleCard
                      key={rule.id}
                      editorKey={`${revision}-${rule.id}`}
                      flag={current}
                      rule={rule}
                      index={index}
                      count={draft.rules.length}
                      disabled={!canEdit}
                      onChange={(patch) => changeRule(rule.id, patch)}
                      onInvalid={(error) => markUnparsed(`rule:${rule.id}`, error)}
                      onMove={(by) => moveRule(index, by)}
                      onDuplicate={() => duplicateRule(index)}
                      onDelete={() => deleteRule(rule.id)}
                    />
                  ))
                )}
              </section>
            </div>

            <div className="xl:sticky xl:top-6 xl:self-start">
              <TestPanel
                flag={current}
                draft={checked.ok ? checked.value : null}
                dirty={dirty}
                savedRules={base.state.rules}
              />
            </div>
          </div>
        </TabsContent>

        <TabsContent value="history" className="pt-4">
          <History flagKey={key} />
        </TabsContent>
      </Tabs>

      {confirming === 'save' && checked.ok ? (
        <SaveDialog
          diff={diffFlagStates(base.state, checked.value)}
          isPending={save.isPending}
          onClose={() => setConfirming(null)}
          onConfirm={() => save.mutate({ state: checked.value, version: base.version })}
        />
      ) : null}

      {confirming === 'reset' ? (
        <Dialog open onOpenChange={(next) => !next && setConfirming(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t('flags.detail.resetTitle')}</DialogTitle>
              <DialogDescription>
                {t('flags.detail.resetBody', { value: formatFlagValue(current.default) })}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setConfirming(null)}>
                {t('common:action.cancel')}
              </Button>
              <Button
                variant="destructive"
                isLoading={reset.isPending}
                onClick={() => reset.mutate()}
              >
                {t('flags.detail.reset')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      ) : null}
    </div>
  );
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0 space-y-1.5">
      <p className="text-xs font-semibold uppercase tracking-wide text-text-muted">{label}</p>
      {children}
    </div>
  );
}

/**
 * A 409 means somebody else wrote first. Reload drops the draft for their
 * version — the only honest way forward, since merging two people's rule
 * lists by guesswork would ship something neither of them chose.
 */
function WriteError({ error, onReload }: { error: unknown; onReload: () => void }) {
  const { t } = useTranslation(['admin', 'common']);
  if (!error) return null;
  const conflict = error instanceof ZuruflexApiError && error.status === 409;
  return (
    <Alert variant={conflict ? 'warning' : 'danger'}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <span>{error instanceof ZuruflexApiError ? error.message : t('common:error.unexpected')}</span>
        {conflict ? (
          <Button size="sm" variant="outline" onClick={onReload}>
            {t('flags.reload')}
          </Button>
        ) : null}
      </div>
    </Alert>
  );
}

// ── Rules ────────────────────────────────────────────────────────────────

function RuleCard({
  editorKey,
  flag,
  rule,
  index,
  count,
  disabled,
  onChange,
  onInvalid,
  onMove,
  onDuplicate,
  onDelete,
}: {
  editorKey: string;
  flag: FlagSummary;
  rule: FlagRule;
  index: number;
  count: number;
  disabled: boolean;
  onChange: (patch: Partial<FlagRule>) => void;
  onInvalid: (error: string | null) => void;
  onMove: (by: -1 | 1) => void;
  onDuplicate: () => void;
  onDelete: () => void;
}) {
  const { t } = useTranslation(['admin', 'common']);
  const versionError =
    rule.minAppVersion && compareVersions(rule.minAppVersion, '0') === null
      ? t('flags.rule.versionInvalid')
      : undefined;

  const toggleIn = <T extends string>(list: T[], item: T, on: boolean): T[] =>
    on ? [...list, item] : list.filter((entry) => entry !== item);

  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-3 space-y-0">
        <div>
          <CardTitle className="flex items-center gap-2">
            {t('flags.rule.name', { n: index + 1 })}
            <span className="font-mono text-xs font-normal text-text-muted">{rule.id}</span>
          </CardTitle>
          {rule.note ? <CardDescription>{rule.note}</CardDescription> : null}
        </div>
        {disabled ? null : (
          <div className="flex items-center gap-1">
            <Button
              size="icon-sm"
              variant="ghost"
              disabled={index === 0}
              aria-label={t('flags.rule.up')}
              onClick={() => onMove(-1)}
            >
              <ArrowUp />
            </Button>
            <Button
              size="icon-sm"
              variant="ghost"
              disabled={index === count - 1}
              aria-label={t('flags.rule.down')}
              onClick={() => onMove(1)}
            >
              <ArrowDown />
            </Button>
            <Button
              size="icon-sm"
              variant="ghost"
              aria-label={t('flags.rule.duplicate')}
              onClick={onDuplicate}
            >
              <Copy />
            </Button>
            <Button
              size="icon-sm"
              variant="ghost"
              aria-label={t('flags.rule.delete')}
              onClick={onDelete}
            >
              <Trash2 />
            </Button>
          </div>
        )}
      </CardHeader>

      <CardContent className="space-y-5">
        <div className="grid gap-5 lg:grid-cols-2 2xl:grid-cols-4">
          <FormField label={t('flags.column.surfaces')} hint={t('flags.rule.emptyMatchesAll')}>
            {() => (
              <div className="flex flex-wrap gap-x-4 gap-y-2">
                {flag.surfaces.map((surface) => (
                  <CheckRow
                    key={surface}
                    label={t(`flags.surface.${surface}`)}
                    checked={rule.surfaces.includes(surface)}
                    disabled={disabled}
                    onChange={(on) =>
                      onChange({ surfaces: toggleIn<FlagSurface>(rule.surfaces, surface, on) })
                    }
                  />
                ))}
              </div>
            )}
          </FormField>

          <FormField label={t('flags.rule.platforms')} hint={t('flags.rule.emptyMatchesAll')}>
            {() => (
              <div className="flex flex-wrap gap-x-4 gap-y-2">
                {FLAG_PLATFORMS.map((platform) => (
                  <CheckRow
                    key={platform}
                    label={t(`flags.platform.${platform}`)}
                    checked={rule.platforms.includes(platform)}
                    disabled={disabled}
                    onChange={(on) =>
                      onChange({ platforms: toggleIn<FlagPlatform>(rule.platforms, platform, on) })
                    }
                  />
                ))}
              </div>
            )}
          </FormField>

          <FormField
            label={t('flags.rule.minVersion')}
            hint={t('flags.rule.minVersionHint')}
            error={versionError}
          >
            {(field) => (
              <Input
                {...field}
                placeholder="1.4.0"
                value={rule.minAppVersion ?? ''}
                disabled={disabled}
                onChange={(event) => onChange({ minAppVersion: event.target.value || null })}
              />
            )}
          </FormField>

          <RolloutField
            key={`${editorKey}-rollout`}
            value={rule.rolloutBasisPoints}
            disabled={disabled}
            onChange={(rolloutBasisPoints) => onChange({ rolloutBasisPoints })}
          />
        </div>

        <div className="grid gap-5 lg:grid-cols-3">
          {ID_FIELDS.map((field) => (
            <IdListField
              key={`${editorKey}-${field}`}
              label={t(`flags.rule.${field}`)}
              ids={rule[field]}
              disabled={disabled}
              onChange={(ids) => onChange({ [field]: ids })}
            />
          ))}
        </div>
        <p className="-mt-2 text-xs text-text-muted">{t('flags.rule.idsHint')}</p>

        <div className="grid gap-5 lg:grid-cols-2">
          <FormField label={t('flags.rule.value')}>
            {(field) => (
              <FlagValueEditor
                key={`${editorKey}-value`}
                id={field.id}
                flag={flag}
                value={rule.value}
                disabled={disabled}
                onChange={(value) => onChange({ value })}
                onInvalid={onInvalid}
              />
            )}
          </FormField>

          <FormField label={t('flags.rule.note')} hint={t('flags.rule.noteHint')}>
            {(field) => (
              <Input
                {...field}
                value={rule.note ?? ''}
                maxLength={500}
                disabled={disabled}
                onChange={(event) => onChange({ note: event.target.value || null })}
              />
            )}
          </FormField>
        </div>
      </CardContent>
    </Card>
  );
}

function CheckRow({
  label,
  checked,
  disabled,
  onChange,
}: {
  label: string;
  checked: boolean;
  disabled: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label className="inline-flex items-center gap-2 text-sm">
      <Checkbox
        checked={checked}
        disabled={disabled}
        onCheckedChange={(next) => onChange(next === true)}
      />
      {label}
    </label>
  );
}

/** Percent in the box, basis points in the rule — 12.34% is 1234. */
function RolloutField({
  value,
  disabled,
  onChange,
}: {
  value: number;
  disabled: boolean;
  onChange: (bps: number) => void;
}) {
  const { t } = useTranslation('admin');
  const [text, setText] = useState(() => rolloutToPercent(value));
  const pct = Number(text);
  const error =
    text.trim() === '' || !Number.isFinite(pct) || pct < 0 || pct > 100
      ? t('flags.rule.rolloutInvalid')
      : undefined;

  return (
    <FormField label={t('flags.rule.rollout')} hint={t('flags.rule.rolloutHint')} error={error}>
      {(field) => (
        <div className="flex items-center gap-2">
          <Input
            {...field}
            type="number"
            inputMode="decimal"
            min={0}
            max={100}
            step={0.01}
            value={text}
            disabled={disabled}
            onChange={(event) => {
              setText(event.target.value);
              onChange(percentToRollout(event.target.value));
            }}
          />
          <span className="text-sm text-text-muted">%</span>
        </div>
      )}
    </FormField>
  );
}

/**
 * Keeps the text as typed and hands up every entry, bad ones included, so
 * the validator refuses a list with a typo rather than it saving shorter.
 */
function IdListField({
  label,
  ids,
  disabled,
  onChange,
}: {
  label: string;
  ids: string[];
  disabled: boolean;
  onChange: (ids: string[]) => void;
}) {
  const { t } = useTranslation('admin');
  const [text, setText] = useState(() => ids.join('\n'));
  const { invalid } = parseIdList(text);

  return (
    <FormField
      label={label}
      error={
        invalid.length
          ? t('flags.rule.idsInvalid', { count: invalid.length, ids: invalid.slice(0, 3).join(', ') })
          : undefined
      }
      hint={ids.length ? t('flags.rule.idsCount', { count: ids.length }) : undefined}
    >
      {(field) => (
        <Textarea
          {...field}
          rows={3}
          spellCheck={false}
          className="font-mono text-xs"
          placeholder="00000000-0000-0000-0000-000000000000"
          value={text}
          disabled={disabled}
          onChange={(event) => {
            setText(event.target.value);
            onChange(parseIdList(event.target.value).ids);
          }}
        />
      )}
    </FormField>
  );
}

// ── Save ─────────────────────────────────────────────────────────────────

function SaveDialog({
  diff,
  isPending,
  onClose,
  onConfirm,
}: {
  diff: FlagStateDiff;
  isPending: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) {
  const { t } = useTranslation(['admin', 'common']);

  return (
    <Dialog open onOpenChange={(next) => !next && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('flags.save.title')}</DialogTitle>
          <DialogDescription>{t('flags.save.body')}</DialogDescription>
        </DialogHeader>
        <DiffSummary diff={diff} />
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            {t('common:action.cancel')}
          </Button>
          <Button isLoading={isPending} onClick={onConfirm}>
            {t('flags.save.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DiffSummary({ diff }: { diff: FlagStateDiff }) {
  const { t } = useTranslation('admin');
  const lines: string[] = [];

  if (diff.enabled) lines.push(t(diff.enabled.after ? 'flags.diff.turnedOn' : 'flags.diff.turnedOff'));
  if (diff.fallthrough) {
    lines.push(
      t('flags.diff.fallthrough', {
        before: formatFlagValue(diff.fallthrough.before),
        after: formatFlagValue(diff.fallthrough.after),
      }),
    );
  }
  if (diff.added) lines.push(t('flags.diff.added', { count: diff.added }));
  if (diff.removed) lines.push(t('flags.diff.removed', { count: diff.removed }));
  if (diff.changed) lines.push(t('flags.diff.changed', { count: diff.changed }));
  if (diff.reordered) lines.push(t('flags.diff.reordered'));
  if (diff.rulesBefore !== diff.rulesAfter) {
    lines.push(t('flags.diff.ruleCount', { before: diff.rulesBefore, after: diff.rulesAfter }));
  }
  if (lines.length === 0) lines.push(t('flags.diff.nothing'));

  return (
    <ul className="list-disc space-y-1 pl-5 text-sm">
      {lines.map((line) => (
        <li key={line} className="break-words">
          {line}
        </li>
      ))}
    </ul>
  );
}

// ── Test ─────────────────────────────────────────────────────────────────

const NONE = 'none';

/**
 * Asks the API what the flag gives one caller — the draft when it is
 * valid, so a rule can be tried before anybody else sees it. The API runs
 * the same `evaluateFlag` the apps are answered with, so this cannot drift
 * from what they will actually get.
 */
function TestPanel({
  flag,
  draft,
  dirty,
  savedRules,
}: {
  flag: FlagSummary;
  /** The draft, when it validates; null tests the saved state instead. */
  draft: FlagState | null;
  dirty: boolean;
  savedRules: FlagRule[];
}) {
  const { t } = useTranslation(['admin', 'common']);
  const [surface, setSurface] = useState<string>(flag.surfaces[0] ?? NONE);
  const [platform, setPlatform] = useState<string>(NONE);
  const [text, setText] = useState({
    appVersion: '',
    customerId: '',
    partnerId: '',
    partnerUserId: '',
    anonymousId: '',
  });
  const [result, setResult] = useState<{ evaluation: FlagEvaluation; rules: FlagRule[] } | null>(
    null,
  );

  const useDraft = dirty && draft !== null;

  const preview = useMutation({
    mutationFn: () => {
      const context: FlagContext = {};
      if (surface !== NONE) context.surface = surface as FlagSurface;
      if (platform !== NONE) context.platform = platform as FlagPlatform;
      for (const [field, value] of Object.entries(text) as [keyof typeof text, string][]) {
        if (value.trim()) context[field] = value.trim();
      }
      return api.previewFlag(flag.key, context, useDraft ? (draft ?? undefined) : undefined);
    },
    onSuccess: (evaluation) =>
      setResult({ evaluation, rules: useDraft && draft ? draft.rules : savedRules }),
  });

  const reasonLabel = (evaluation: FlagEvaluation, rules: FlagRule[]): string => {
    if (evaluation.reason.startsWith('rule:')) {
      const id = evaluation.reason.slice('rule:'.length);
      const index = rules.findIndex((rule) => rule.id === id);
      return index >= 0 ? t('flags.rule.name', { n: index + 1 }) : evaluation.reason;
    }
    return t(`flags.test.reason.${evaluation.reason}`);
  };

  const textField = (field: keyof typeof text, label: string, placeholder?: string) => (
    <FormField label={label}>
      {(props) => (
        <Input
          {...props}
          value={text[field]}
          placeholder={placeholder}
          className="font-mono text-xs"
          onChange={(event) => setText((prev) => ({ ...prev, [field]: event.target.value }))}
        />
      )}
    </FormField>
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FlaskConical className="size-4" />
          {t('flags.test.title')}
        </CardTitle>
        <CardDescription>
          {useDraft
            ? t('flags.test.usingDraft')
            : dirty
              ? t('flags.test.draftInvalid')
              : t('flags.test.usingSaved')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <FormField label={t('flags.test.surface')}>
            {(field) => (
              <Select value={surface} onValueChange={setSurface}>
                <SelectTrigger id={field.id}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>{t('flags.test.unset')}</SelectItem>
                  {flag.surfaces.map((value) => (
                    <SelectItem key={value} value={value}>
                      {t(`flags.surface.${value}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </FormField>
          <FormField label={t('flags.rule.platform')}>
            {(field) => (
              <Select value={platform} onValueChange={setPlatform}>
                <SelectTrigger id={field.id}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>{t('flags.test.unset')}</SelectItem>
                  {FLAG_PLATFORMS.map((value) => (
                    <SelectItem key={value} value={value}>
                      {t(`flags.platform.${value}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </FormField>
        </div>
        {textField('appVersion', t('flags.test.appVersion'), '1.4.0')}
        {textField('customerId', t('flags.test.customerId'))}
        {textField('partnerId', t('flags.test.partnerId'))}
        {textField('partnerUserId', t('flags.test.partnerUserId'))}
        {textField('anonymousId', t('flags.test.anonymousId'))}

        <Button className="w-full" isLoading={preview.isPending} onClick={() => preview.mutate()}>
          {t('flags.test.run')}
        </Button>

        {preview.error ? (
          <Alert variant="danger">
            {preview.error instanceof ZuruflexApiError
              ? preview.error.message
              : t('common:error.unexpected')}
          </Alert>
        ) : result ? (
          <div className="space-y-2 rounded-card bg-surface-sunken p-4">
            <div className="flex items-center justify-between gap-2 text-sm">
              <span className="text-text-muted">{t('flags.test.because')}</span>
              <Badge variant="soft">{reasonLabel(result.evaluation, result.rules)}</Badge>
            </div>
            <pre className="max-h-64 overflow-auto whitespace-pre-wrap break-all font-mono text-xs">
              {JSON.stringify(result.evaluation.value, null, 2)}
            </pre>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

// ── History ──────────────────────────────────────────────────────────────

function History({ flagKey }: { flagKey: string }) {
  const { t, i18n } = useTranslation(['admin', 'common']);
  const locale = toLocale(i18n.language);
  const history = useQuery({
    queryKey: ['flag', flagKey, 'history'],
    queryFn: () => api.flagHistory(flagKey, 50),
  });

  if (history.error) {
    return <ErrorState error={history.error} onRetry={() => void history.refetch()} />;
  }
  if (history.isPending) return <Skeleton className="h-48 w-full" />;
  if (history.data.length === 0) {
    return <p className="text-sm text-text-muted">{t('flags.history.none')}</p>;
  }

  return (
    <Card>
      <CardContent className="divide-y divide-border-default p-0">
        {history.data.map((entry) => (
          <HistoryRow
            key={entry.id}
            flagKey={flagKey}
            entry={entry}
            when={relativeTime(entry.createdAt, locale)}
            exact={formatDateTimeIn(entry.createdAt, locale)}
          />
        ))}
      </CardContent>
    </Card>
  );
}

function HistoryRow({
  flagKey,
  entry,
  when,
  exact,
}: {
  flagKey: string;
  entry: FlagChange;
  when: string;
  exact: string;
}) {
  const { t } = useTranslation('admin');
  // No row on one side means the flag behaved as never set — compare
  // against that, or a first save reads as "fallthrough null → false".
  const unset = isFlagKey(flagKey) ? emptyFlagState(flagKey) : null;
  const diff = entry.diff
    ? diffFlagStates(entry.diff.before ?? unset, entry.diff.after ?? unset)
    : null;
  // A string, not the union: the API can grow an action before this does.
  const action = entry.action as string;
  const after = entry.diff?.after ?? null;
  const label =
    action === 'feature_flag.toggle'
      ? t(after?.enabled ? 'flags.history.switchedOn' : 'flags.history.switchedOff')
      : t(`flags.history.action.${action.replace('feature_flag.', '')}`, action);

  return (
    <div className="grid gap-3 px-6 py-4 lg:grid-cols-[260px_minmax(0,1fr)]">
      <div className="min-w-0">
        <p className="truncate text-sm font-medium">
          {entry.actor?.fullName ?? t('flags.history.system')}
        </p>
        {entry.actor ? (
          <p className="truncate text-xs text-text-muted">{entry.actor.email}</p>
        ) : null}
        <p className="mt-1 text-xs text-text-muted" title={exact}>
          {when} · {exact}
        </p>
      </div>
      <div className="min-w-0 space-y-2">
        <Badge variant={action === 'feature_flag.reset' ? 'warning' : 'outline'}>{label}</Badge>
        {/* A toggle's badge already says everything it changed. */}
        {diff && action !== 'feature_flag.toggle' ? <DiffSummary diff={diff} /> : null}
        {entry.diff ? (
          <details className="text-xs">
            <summary className="cursor-pointer text-text-muted hover:text-text">
              {t('flags.history.raw')}
            </summary>
            <div className="mt-2 grid gap-3 xl:grid-cols-2">
              {(['before', 'after'] as const).map((side) => (
                <div key={side} className="min-w-0">
                  <p className="mb-1 font-semibold">{t(`flags.history.${side}`)}</p>
                  <pre className="max-h-80 overflow-auto rounded-card bg-surface-sunken p-3 font-mono">
                    {JSON.stringify(entry.diff?.[side] ?? null, null, 2)}
                  </pre>
                </div>
              ))}
            </div>
          </details>
        ) : null}
      </div>
    </div>
  );
}

